export const MEASUREMENT_ID = 'G-J5DPZ9874S';
export const CONSENT_KEY = 'crestia_analytics_consent_v1';
export const CONSENT_SETTINGS_EVENT = 'crestia:cookie-settings';
const MAX_AGE = 180 * 86400000;
const ORIGIN = 'https://crestia.gr';
const PAGE_TITLES = {
  '/': 'Αρχική', '/services': 'Υπηρεσίες', '/who-we-serve': 'Σε ποιους απευθυνόμαστε',
  '/approach': 'Προσέγγιση', '/insights': 'Άρθρα', '/contact': 'Επικοινωνία',
  '/privacy-policy': 'Πολιτική Απορρήτου', '/terms': 'Όροι Χρήσης', '/cookies': 'Πολιτική Cookies',
};

// Send only known public paths and fixed titles, never query strings, fragments or user input.
function safePage(value) {
  const path = String(value).split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  const title = PAGE_TITLES[path] || (/^\/insights\/[a-z0-9]+(?:-[a-z0-9]+)*$/.test(path) && path.length < 180 ? 'Άρθρο' : null);
  // Article URLs are grouped: arbitrary unverified slugs must not disclose personal data.
  return title ? { path, page_location: ORIGIN + (PAGE_TITLES[path] ? path : '/insights'), page_title: `${title} | CRESTIA` } : null;
}

export function createAnalyticsController({ win = window, doc = document, now = Date.now } = {}) {
  let storageAvailable = true;
  const parse = value => {
    try {
      const record = JSON.parse(value);
      return record?.version === 1 && typeof record.analytics === 'boolean' && Number.isFinite(record.updatedAt)
        && record.updatedAt <= now() && now() - record.updatedAt < MAX_AGE ? record : null;
    } catch { return null; }
  };
  let preference;
  try {
    const saved = win.localStorage.getItem(CONSENT_KEY);
    preference = parse(saved);
    // Never restore acceptance from a store that can no longer save its withdrawal.
    if (preference?.analytics) win.localStorage.setItem(CONSENT_KEY, saved);
  }
  catch { preference = null; storageAvailable = false; }
  let currentPage = null;
  let lastPage = null;
  let epoch = 0;
  let loaded = false;
  let loading = false;
  let configured = false;
  let started = false;
  let expiryTimer;
  const listeners = new Set();
  const disabledKey = `ga-disable-${MEASUREMENT_ID}`;
  const state = () => ({ choice: preference ? (preference.analytics ? 'accepted' : 'rejected') : 'unknown', storageAvailable });
  const notify = () => listeners.forEach(listener => listener(state()));

  function clearAnalyticsCookies() {
    const hostname = win.location.hostname;
    const domains = ['', ...hostname.split('.').map((_, index, parts) => parts.slice(index).join('.')).filter(domain => domain.includes('.'))];
    doc.cookie.split(';').map(cookie => cookie.trim().split('=')[0]).filter(name => /^_ga(?:_|$)/.test(name)).forEach(name => {
      domains.forEach(domain => {
        doc.cookie = `${name}=; Max-Age=0; path=/;${domain ? ` domain=${domain};` : ''} SameSite=Lax`;
      });
    });
  }

  function disable() {
    // This documented GA opt-out flag also blocks the already-loaded tag. No withdrawal ping.
    win[disabledKey] = true;
    lastPage = null;
    clearAnalyticsCookies();
  }

  function allowed() {
    if (preference && !parse(JSON.stringify(preference))) {
      preference = null; epoch += 1; disable(); notify();
    }
    return preference?.analytics === true;
  }

  function pageView() {
    if (!allowed() || !loaded || !currentPage || lastPage?.path === currentPage.path) return;
    const { path, ...page } = currentPage;
    win.gtag('set', { ...page, page_referrer: lastPage?.page_location || '' });
    win.gtag('event', 'page_view', { ...page, page_referrer: lastPage?.page_location || '' });
    lastPage = currentPage;
  }

  function activate() {
    if (!allowed() || !loaded) return;
    win[disabledKey] = false;
    win.gtag('consent', 'update', { analytics_storage: 'granted', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
    if (!configured) {
      win.gtag('set', {
        page_location: currentPage?.page_location || ORIGIN + '/',
        page_referrer: '',
        page_title: currentPage?.page_title || 'CRESTIA',
      });
      win.gtag('config', MEASUREMENT_ID, {
        send_page_view: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
        cookie_expires: MAX_AGE / 1000,
        cookie_update: false,
      });
      configured = true;
    }
    pageView();
  }

  function ensureTag() {
    if (!allowed()) return;
    if (loaded) { activate(); return; }
    if (loading) return;
    loading = true;
    win[disabledKey] = false;
    win.dataLayer = win.dataLayer || [];
    win.gtag = win.gtag || function () { win.dataLayer.push(arguments); };
    win.gtag('consent', 'default', { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
    win.gtag('js', new Date(now()));
    const script = doc.createElement('script');
    script.async = true;
    script.dataset.crestiaAnalytics = 'true';
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    script.onload = () => { loaded = true; loading = false; activate(); };
    script.onerror = () => { loading = false; script.remove(); };
    doc.head.appendChild(script);
  }

  function transition(record) {
    const previous = preference?.analytics;
    preference = record;
    if (previous !== preference?.analytics) { epoch += 1; lastPage = null; }
    if (allowed()) ensureTag(); else disable();
    notify();
  }

  function onStorage(event) {
    if (event.key === CONSENT_KEY || event.key === null) transition(parse(event.newValue));
  }

  function onVisibility() { if (doc.visibilityState === 'visible') allowed(); }

  return {
    getState() { allowed(); return state(); },
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    start() {
      if (started) return;
      started = true;
      win.addEventListener('storage', onStorage);
      doc.addEventListener('visibilitychange', onVisibility);
      expiryTimer = win.setInterval(allowed, 60000);
      if (allowed()) ensureTag(); else disable();
    },
    stop() {
      started = false;
      win.removeEventListener('storage', onStorage);
      doc.removeEventListener('visibilitychange', onVisibility);
      win.clearInterval(expiryTimer);
    },
    choose(analytics) {
      const record = { version: 1, analytics: analytics === true, updatedAt: now() };
      try { win.localStorage.setItem(CONSENT_KEY, JSON.stringify(record)); }
      catch {
        storageAvailable = false;
        try { win.localStorage.removeItem(CONSENT_KEY); } catch { /* Restoring acceptance also requires writable storage. */ }
      }
      transition(record);
    },
    setPage(pathname) {
      currentPage = safePage(pathname);
      if (allowed()) { if (!loaded) ensureTag(); else pageView(); }
    },
    consentToken() { return allowed() ? epoch : null; },
    trackLead(token) {
      if (token === null || token !== epoch || !allowed() || !loaded || !currentPage) return;
      win.gtag('event', 'generate_lead', { form_id: 'contact', page_location: currentPage.page_location });
    },
  };
}

// Observe successful requests without reading form data or changing form behavior.
export function observeContactSubmissions(client, controller, api) {
  const pending = new WeakMap();
  const contact = new URL(`${api}/contact`, window.location.origin);
  const requestId = client.interceptors.request.use(config => {
    try {
      const url = new URL(config.url, config.baseURL || window.location.origin);
      if (config.method?.toLowerCase() === 'post' && url.origin === contact.origin && url.pathname === contact.pathname) {
        pending.set(config, controller.consentToken());
      }
    } catch { /* Analytics must never interrupt a request. */ }
    return config;
  });
  const responseId = client.interceptors.response.use(response => {
    try {
      if (pending.has(response.config)) {
        const token = pending.get(response.config); pending.delete(response.config);
        if (response.status >= 200 && response.status < 300) controller.trackLead(token);
      }
    } catch { /* Analytics must never alter the successful response. */ }
    return response;
  });
  return () => { client.interceptors.request.eject(requestId); client.interceptors.response.eject(responseId); };
}

let singleton;
export const getAnalytics = () => singleton || (singleton = createAnalyticsController());
export const openConsentSettings = () => window.dispatchEvent(new Event(CONSENT_SETTINGS_EVENT));
